import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight, Landmark } from 'lucide-react';
import { Heading } from '../../layout';
import { PillTabBar } from '../../reusable';
import { useUIColors } from '../../../hooks/useTheme';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  getAvailableYearsFromDates,
  parseDateSafe,
  resolvePeriodSelection,
  resolveChartAxisColumns,
  periodLabelStyle,
  periodSelectStyle,
} from '../../placeholders/periodSummaryUtils';
import { getTreasuryAccounts, getTreasuryEntries } from '../../../services/treasuryAPI';
import AccountingMonthlyChart from './accounting/AccountingMonthlyChart';
import AccountingPeriodSummary from './accounting/AccountingPeriodSummary';
import TreasuryMovementsPanel from './treasury/TreasuryMovementsPanel';
import TreasuryAccountsPanel from './treasury/TreasuryAccountsPanel';
import TreasuryOverview from './treasury/TreasuryOverview';

const NAV_ITEMS = [
  { key: 'movements', label: 'Movimientos bancarios', icon: ArrowLeftRight },
  { key: 'accounts', label: 'Cuentas bancarias', icon: Landmark },
];

const PERIOD_TITLES = {
  monthly: 'Resumen movimientos bancarios',
  annual: 'Resumen movimientos bancarios anual',
};

const YEAR_SCOPED_PERIODS = new Set(['annual', 'annual_ytd', 'quarterly', 'semestral']);

const FinanceTreasuryWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [section, setSection] = useState('movements');
  const [revision, setRevision] = useState(0);
  const [periodFilter, setPeriodFilter] = useState('ytd');
  const [secondaryYear, setSecondaryYear] = useState(String(new Date().getFullYear()));
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [dateValues, setDateValues] = useState([]);
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  const notifyChanged = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadError('');
        const [entriesPayload, accountsPayload] = await Promise.all([getTreasuryEntries(), getTreasuryAccounts()]);
        if (cancelled) return;
        setEntries(Array.isArray(entriesPayload) ? entriesPayload : []);
        setAccounts(Array.isArray(accountsPayload) ? accountsPayload : []);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err.message || 'No se pudo cargar tesorería.');
        setEntries([]);
        setAccounts([]);
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [revision]);

  const handleDateValuesChange = useCallback((dates) => {
    setDateValues((prev) => {
      const next = Array.isArray(dates) ? dates : [];
      if (
        prev.length === next.length &&
        prev.every((date, index) => date?.getTime?.() === next[index]?.getTime?.())
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const dates = entries.map((row) => parseDateSafe(row.entry_date)).filter(Boolean);
    handleDateValuesChange(dates);
  }, [entries, handleDateValuesChange]);

  const availableYears = useMemo(() => getAvailableYearsFromDates(dateValues), [dateValues]);

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues,
        secondaryYear,
        secondaryMonth,
        titles: PERIOD_TITLES,
      }),
    [periodFilter, dateValues, secondaryYear, secondaryMonth]
  );

  const chartColumns = useMemo(() => resolveChartAxisColumns(selectedPeriod), [selectedPeriod]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (YEAR_SCOPED_PERIODS.has(periodFilter)) {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly' && !availableYears.includes(Number(secondaryYear))) {
      setSecondaryYear(String(availableYears[availableYears.length - 1]));
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (YEAR_SCOPED_PERIODS.has(nextPeriod)) {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const showPeriodControls = section === 'movements';
  const isLoading = !hasLoaded;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: '16px',
          rowGap: '12px',
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            Tesorería
          </Heading>
        </div>
        {showPeriodControls ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              gap: '10px',
              flexWrap: 'nowrap',
              justifySelf: 'end',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Período</span>
              <select
                value={periodFilter}
                onChange={(event) => handlePeriodFilterChange(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {YEAR_SCOPED_PERIODS.has(periodFilter) ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={secondaryYear}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
                >
                  <option value="all">Todas</option>
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {periodFilter === 'monthly' ? (
              <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                  <span style={periodLabelStyle(ui)}>Año</span>
                  <select
                    value={String(
                      secondaryYear === 'all'
                        ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                        : secondaryYear
                    )}
                    onChange={(event) => setSecondaryYear(event.target.value)}
                    style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                  <span style={periodLabelStyle(ui)}>Mes</span>
                  <select
                    value={secondaryMonth}
                    onChange={(event) => setSecondaryMonth(event.target.value)}
                    style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                  >
                    {MONTH_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <PillTabBar
        ariaLabel="Secciones de tesorería"
        items={NAV_ITEMS}
        activeKey={section}
        onChange={setSection}
        style={{ marginBottom: 16 }}
      />

      {section === 'movements' ? (
        <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
          <TreasuryOverview
            entries={entries}
            accounts={accounts}
            selectedPeriod={selectedPeriod}
            chartColumns={chartColumns}
            periodFilter={periodFilter}
            periodTitle={
              PERIOD_OPTIONS.find((option) => option.value === periodFilter)?.label || selectedPeriod?.title || ''
            }
            isDarkMode={isDarkMode}
            ui={ui}
            loading={isLoading}
          >
            <AccountingMonthlyChart
              section="treasury"
              refreshKey={revision}
              periods={chartColumns}
              periodRange={selectedPeriod.range}
              selectedPeriod={selectedPeriod}
              onDateValuesChange={handleDateValuesChange}
              documents={entries}
              documentsLoading={isLoading}
              documentsError={loadError}
            />
          </TreasuryOverview>
          <AccountingPeriodSummary
            section="treasury"
            refreshKey={revision}
            selectedPeriod={selectedPeriod}
            periodTitle={
              selectedPeriod?.title ||
              (periodFilter === 'all' ? `${PERIOD_TITLES.monthly} · todos los períodos` : PERIOD_TITLES.monthly)
            }
            documents={entries}
            documentsLoading={isLoading}
            documentsError={loadError}
          />
          <TreasuryMovementsPanel
            ui={ui}
            isDarkMode={isDarkMode}
            entries={entries}
            accounts={accounts}
            loading={isLoading}
            error={loadError}
            selectedPeriod={selectedPeriod}
            onChanged={notifyChanged}
            onGoToAccounts={() => setSection('accounts')}
          />
        </div>
      ) : (
        <TreasuryAccountsPanel
          ui={ui}
          isDarkMode={isDarkMode}
          accounts={accounts}
          loading={isLoading}
          error={loadError}
          onChanged={notifyChanged}
        />
      )}
    </div>
  );
};

export default FinanceTreasuryWorkspace;
